import React, { useMemo } from "react";
import { useLoadPublicProductCollection } from "../../Utils/customHooks/useLoadPublicProductCollection";
import { ShopBrowseNav } from "../DashboardView/MasterTemplateEditor/ShopBrowseNav";
import { ShopProductCatalogPanel } from "../DashboardView/MasterTemplateEditor/templatePages/ShopProductCatalogPanel";
import { mapFeedPostToShopCatalogProduct } from "../DashboardView/MasterTemplateEditor/templateShopCatalog";
import type { ThemeTokens } from "../DashboardView/MasterTemplateEditor/types";
import type { ShopCatalogProduct } from "../DashboardView/MasterTemplateEditor/templateShopCatalog";
import "../DashboardView/MasterTemplateEditor/template.css";

type CategoryOption = {
  slug: string;
  label: string;
};

export default function WebsiteProductCollectionPanel({
  T,
  collectionId,
  username,
  userId,
  cols,
  aspect,
  showButton,
  showCategory,
  showPrice,
  buttonLabel,
  brandCounts,
  categoryOptions,
  onClear,
  onPickCategory,
}: {
  T: ThemeTokens;
  collectionId: string;
  username: string;
  userId: string;
  cols: number;
  aspect: string;
  showButton: boolean;
  showCategory: boolean;
  showPrice: boolean;
  buttonLabel: string;
  brandCounts?: Map<string, number>;
  categoryOptions: CategoryOption[];
  onClear: () => void;
  onPickCategory: (slug: string, label: string) => void;
}) {
  const isMobile = T.device === "mobile";
  const { data, isLoading } = useLoadPublicProductCollection({
    collectionId,
    username,
  });
  const collection = data?.collection;
  const title = String(collection?.name || "").trim();
  const description = String(collection?.description || "").trim();

  const products = useMemo(() => {
    const rows = Array.isArray(data?.products) ? data.products : [];
    const mapped: ShopCatalogProduct[] = [];
    for (const row of rows) {
      const product = mapFeedPostToShopCatalogProduct({
        ...row,
        type: row?.type || "PRODUCT",
      });
      if (product) mapped.push(product);
    }
    return mapped;
  }, [data?.products]);

  const crumbs = title ? [{ label: title, onClear }] : [];
  const options = categoryOptions.map((option) => ({
    label: option.label,
    onClick: () => onPickCategory(option.slug, option.label),
  }));

  return (
    <div
      className="mte-tpl-product-collection-page"
      data-website-region="shop-region"
    >
      {crumbs.length || options.length ? (
        <div className="mte-tpl-shop-browse-enter-wrap">
          <ShopBrowseNav crumbs={crumbs} options={options} isMobile={isMobile} />
        </div>
      ) : null}
      {title ? (
        <div className="mte-tpl-product-collection-hero">
          <div className="mte-tpl-product-collection-hero-title">{title}</div>
          {description ? (
            <p className="mte-tpl-product-collection-hero-sub">{description}</p>
          ) : null}
        </div>
      ) : null}
      <ShopProductCatalogPanel
        T={T}
        products={products}
        loading={isLoading}
        userId={userId}
        cols={cols}
        aspect={aspect}
        showButton={showButton}
        showCategory={showCategory}
        showPrice={showPrice}
        buttonLabel={buttonLabel}
        shopView="all"
        catalogKey={`product-collection:${collectionId}`}
        brandCounts={brandCounts}
      />
    </div>
  );
}
